/**
 * One chip, every vocabulary.
 *
 * `packages/shared/src/vocabulary.ts` says what a code reads as and how
 * loudly; this is the only thing that turns a tone into a class name. Every
 * panel screen used to carry its own `Record<string, string>` of `'chip'`,
 * `'chip-resolved'`, `'chip chip-danger'` — note the two spellings, one of
 * which quietly dropped the base class — and its own inline
 * `<span className={...}>`.
 *
 * A screen now names the vocabulary and passes the code. Adding a state means
 * one line in `shared`, and every screen that shows that column follows.
 */

import type { Tone, Vocabulary } from '@content-removal/shared';
import { termOf } from '@content-removal/shared';

/**
 * `neutral` has no modifier of its own: the base `.chip` rule is the neutral
 * look, and a `.chip-neutral` that existed only to be empty is a class
 * somebody eventually gives a colour to by mistake.
 */
const TONE_CLASS: Record<Tone, string> = {
  neutral: '',
  progress: 'chip-progress',
  resolved: 'chip-resolved',
  attention: 'chip-attention',
  danger: 'chip-danger',
};

/** The class list for a tone, for the rare caller that draws its own element. */
export function toneClass(tone: Tone): string {
  return TONE_CLASS[tone];
}

export function Chip({
  vocabulary,
  code,
  className,
}: {
  vocabulary: Vocabulary;
  code: string | null | undefined;
  /** Extra classes for layout only. Colour comes from the tone, never from here. */
  className?: string;
}) {
  const term = termOf(vocabulary, code);
  return <span className={['chip', TONE_CLASS[term.tone], className].filter(Boolean).join(' ')}>{term.label}</span>;
}
